import { computeCPA, normaliseBearing, projectPosition } from "../geodesy.js";
import type {
  BehaviourImpl,
  BehaviourPose,
  BehaviourRuntime,
} from "./types.js";

export interface ColregsGiveWayParams {
  targetVesselId: string;
}

const SECONDS_PER_HOUR = 3600;
/** CPA threshold below which we initiate a giveway turn. */
const GIVEWAY_CPA_THRESHOLD_NM = 0.5;
/** TCPA window during which we're worried about the encounter. */
const GIVEWAY_TCPA_WINDOW_MIN = 8;
/** Standard COLREGS giveway turn — to starboard, well-defined and visible. */
const GIVEWAY_TURN_DEG = 30;

/**
 * Phase-1 baseline COLREGS giveway. Vessel proceeds on its spawn course at
 * spawn speed. When CPA with the named target falls below a threshold and
 * TCPA is in the worry window, the vessel turns 30° starboard until the
 * encounter has resolved (CPA rises or TCPA goes negative). Once clear, the
 * vessel returns to its original course.
 *
 * This is a simplified rule, not full COLREGS — week 3-4 will refine target
 * physics including richer giveway behaviours (port-bow vs starboard-bow,
 * crossing vs overtaking, slow-down options).
 */
export const ColregsGiveWay: BehaviourImpl<ColregsGiveWayParams> = {
  name: "colregs_giveway",
  version: "1.0.0",
  init({ params, spawn, spawnSimSeconds }): BehaviourRuntime {
    return new ColregsGiveWayRuntime(
      params.targetVesselId,
      spawn,
      spawnSimSeconds,
    );
  },
};

class ColregsGiveWayRuntime implements BehaviourRuntime {
  private readonly targetVesselId: string;
  private readonly spawn: BehaviourPose;
  private readonly spawnSimSeconds: number;

  // Forward-only cursor. Replays must construct a fresh runtime.
  private cursorSeconds: number;
  private cursorPose: BehaviourPose;
  /** True when currently executing the giveway turn. */
  private inGiveway = false;

  constructor(
    targetVesselId: string,
    spawn: BehaviourPose,
    spawnSimSeconds: number,
  ) {
    this.targetVesselId = targetVesselId;
    this.spawn = spawn;
    this.spawnSimSeconds = spawnSimSeconds;
    this.cursorSeconds = spawnSimSeconds;
    this.cursorPose = { ...spawn };
  }

  poseAt(simSeconds: number, ctx: import("./types.js").BehaviourContext): BehaviourPose {
    if (simSeconds < this.cursorSeconds) {
      // Backward seek: rewind to spawn (deterministic restart).
      this.cursorSeconds = this.spawnSimSeconds;
      this.cursorPose = { ...this.spawn };
      this.inGiveway = false;
    }
    const dt = simSeconds - this.cursorSeconds;
    if (dt <= 0) return { ...this.cursorPose };

    const target = ctx.otherVessels.get(this.targetVesselId);
    if (target !== undefined) {
      const cpa = computeCPA(
        {
          position: this.cursorPose.position,
          cog: this.cursorPose.cog,
          sog: this.cursorPose.sog,
        },
        target,
      );
      const worried =
        cpa.cpaNm < GIVEWAY_CPA_THRESHOLD_NM &&
        cpa.tcpaMin > 0 &&
        cpa.tcpaMin < GIVEWAY_TCPA_WINDOW_MIN;
      if (worried && !this.inGiveway) {
        this.cursorPose = {
          ...this.cursorPose,
          cog: normaliseBearing(this.cursorPose.cog + GIVEWAY_TURN_DEG),
        };
        this.inGiveway = true;
      } else if (!worried && this.inGiveway) {
        // Encounter resolved: return to original course.
        this.cursorPose = { ...this.cursorPose, cog: this.spawn.cog };
        this.inGiveway = false;
      }
    }

    const distNm = this.cursorPose.sog * (dt / SECONDS_PER_HOUR);
    if (distNm > 0) {
      this.cursorPose = {
        ...this.cursorPose,
        position: projectPosition(
          this.cursorPose.position,
          this.cursorPose.cog,
          distNm,
        ),
      };
    }
    this.cursorSeconds = simSeconds;
    return { ...this.cursorPose };
  }
}
